import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LinkedUser } from "@/types/linkedUsers";

export const useTaskLinkedUsers = (taskId: string | undefined) => {
	return useQuery({
		queryKey: ["task-linked-users", taskId],
		queryFn: async () => {
			if (!taskId) return [];
			
			const { data, error } = await supabase
				.rpc("get_task_linked_users", { p_task_id: taskId });
			
			if (error) throw error;
			return data as LinkedUser[];
		},
		enabled: !!taskId,
	});
};

export const useEventLinkedUsers = (eventId: string | undefined) => {
	return useQuery({
		queryKey: ["event-linked-users", eventId],
		queryFn: async () => {
			if (!eventId) return [];
			
			const { data, error } = await supabase
				.rpc("get_event_linked_users", { p_event_id: eventId });
			
			if (error) throw error;
			return data as LinkedUser[];
		},
		enabled: !!eventId,
	});
};

export const useLinkUserToTask = () => {
	const queryClient = useQueryClient();
	
	return useMutation({
		mutationFn: async ({ taskId, userId }: { taskId: string; userId: string }) => {
			const { data, error } = await supabase
				.rpc("link_user_to_task", { 
					p_task_id: taskId, 
					p_user_id: userId 
				});
			
			if (error) throw error;
			return data;
		},
		onSuccess: (_, { taskId }) => {
			queryClient.invalidateQueries({ queryKey: ["task-linked-users", taskId] });
			queryClient.invalidateQueries({ queryKey: ["actions"] });
			toast.success("User linked successfully");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to link user");
		},
	});
};

export const useUnlinkUserFromTask = () => {
	const queryClient = useQueryClient();
	
	return useMutation({
		mutationFn: async ({ taskId, userId }: { taskId: string; userId: string }) => {
			const { data, error } = await supabase
				.rpc("unlink_user_from_task", { 
					p_task_id: taskId, 
					p_user_id: userId 
				});
			
			if (error) throw error;
			return data;
		},
		onSuccess: (_, { taskId }) => {
			queryClient.invalidateQueries({ queryKey: ["task-linked-users", taskId] });
			queryClient.invalidateQueries({ queryKey: ["actions"] });
			toast.success("User unlinked successfully");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to unlink user");
		},
	});
};
